// ============================================================================
// Multi-Tenant Enterprise Unique SKU & Deterministic MMYTE Taxonomy Engine (Marketplace)
// Architecture Standard:
// P4D-[VendorAbbreviation]-[Country]-[Region/State]-[Make]-[Model]-[Year]-[Category]-[Subcategory]
// Example: P4D-AB-GH-ACC-TY-CO-17-BDY-BMP
// ============================================================================

export const SKU_PREFIX = 'P4D'

export interface MmyteSubcategory {
  code: string
  legacyCode?: string
  name: string
  shortName?: string
}

export interface MmyteCategory {
  code: string
  name: string
  subcategories: MmyteSubcategory[]
}

export interface ConditionGrade {
  code: string
  name: string
  badge: string
  description: string
}

export const MMYTE_CATEGORIES: MmyteCategory[] = [
  {
    code: 'BRK',
    name: 'Braking & Traction',
    subcategories: [
      { code: 'BMP', legacyCode: 'BRKP', name: 'Brake Pads & Shoes', shortName: 'Pads' },
      { code: 'ROT', legacyCode: 'BRKD', name: 'Brake Rotors & Drums', shortName: 'Rotors' },
      { code: 'CAL', legacyCode: 'BRKC', name: 'Calipers & Cylinders', shortName: 'Calipers' },
      { code: 'HOS', legacyCode: 'BRKH', name: 'Hydraulic Hoses & Lines', shortName: 'Hoses' },
      { code: 'ABS', legacyCode: 'BRKB', name: 'ABS Sensors & Modules', shortName: 'ABS' }
    ]
  },
  {
    code: 'ENG',
    name: 'Powertrain & Engine',
    subcategories: [
      { code: 'PST', legacyCode: 'ENGP', name: 'Pistons, Rings & Liners', shortName: 'Pistons' },
      { code: 'GSK', legacyCode: 'ENGG', name: 'Gaskets & Engine Seals', shortName: 'Gaskets' },
      { code: 'TIM', legacyCode: 'ENGT', name: 'Timing Belts & Chains', shortName: 'Timing' },
      { code: 'INJ', legacyCode: 'ENGF', name: 'Fuel Injectors & Pumps', shortName: 'Fuel Injection' },
      { code: 'OIL', legacyCode: 'ENGO', name: 'Oil Pumps & Filtration', shortName: 'Oil System' }
    ]
  },
  {
    code: 'SUS',
    name: 'Suspension & Steering',
    subcategories: [
      { code: 'ARM', legacyCode: 'SUSC', name: 'Control Arms & Ball Joints', shortName: 'Control Arms' },
      { code: 'SHK', legacyCode: 'SUSS', name: 'Shock Absorbers & Struts', shortName: 'Shocks' },
      { code: 'RCK', legacyCode: 'SUSR', name: 'Steering Rack & Pinion', shortName: 'Steering' },
      { code: 'BSH', legacyCode: 'SUSB', name: 'Bushings & Stabilizer Links', shortName: 'Bushings' },
      { code: 'HUB', legacyCode: 'SUSH', name: 'Wheel Hubs & Bearings', shortName: 'Hubs' }
    ]
  },
  {
    code: 'COL',
    name: 'Cooling & Climate',
    subcategories: [
      { code: 'RAD', legacyCode: 'COLR', name: 'Radiators & Evaporators', shortName: 'Radiators' },
      { code: 'WTR', legacyCode: 'COLW', name: 'Water Pumps & Thermostats', shortName: 'Water Pumps' },
      { code: 'CMP', legacyCode: 'COLC', name: 'A/C Compressors & Clutches', shortName: 'Compressors' },
      { code: 'FAN', legacyCode: 'COLF', name: 'Cooling Fans & Motors', shortName: 'Fans' }
    ]
  },
  {
    code: 'TRN',
    name: 'Transmission & Drivetrain',
    subcategories: [
      { code: 'CLT', legacyCode: 'TRNC', name: 'Clutch Kits & Flywheels', shortName: 'Clutch' },
      { code: 'VLV', legacyCode: 'TRNA', name: 'Automatic Transmission Valve Bodies', shortName: 'Valve Bodies' },
      { code: 'AXL', legacyCode: 'TRND', name: 'CV Axles & Driveshafts', shortName: 'Axles' },
      { code: 'DIF', legacyCode: 'TRNF', name: 'Differential & Transfer Cases', shortName: 'Differential' }
    ]
  },
  {
    code: 'ELC',
    name: 'Electrical & Lighting',
    subcategories: [
      { code: 'ALT', legacyCode: 'ELCA', name: 'Alternators & Regulators', shortName: 'Alternators' },
      { code: 'STR', legacyCode: 'ELCS', name: 'Starter Motors', shortName: 'Starters' },
      { code: 'LMP', legacyCode: 'ELCH', name: 'Headlamps & Tail Lights', shortName: 'Lights' },
      { code: 'ECU', legacyCode: 'ELCE', name: 'ECU & Engine Modules', shortName: 'Modules' },
      { code: 'IGN', legacyCode: 'ELCI', name: 'Ignition Coils & Plugs', shortName: 'Ignition' }
    ]
  },
  {
    code: 'BDY',
    name: 'Body & Structural Panels',
    subcategories: [
      { code: 'BMP', legacyCode: 'BDYF', name: 'Bumpers & Grilles', shortName: 'Bumpers' },
      { code: 'HOD', legacyCode: 'BDYH', name: 'Hoods, Fenders & Quarter Panels', shortName: 'Hoods/Fenders' },
      { code: 'DOR', legacyCode: 'BDYD', name: 'Doors & Tailgates', shortName: 'Doors' },
      { code: 'MIR', legacyCode: 'BDYM', name: 'Side Mirrors & Glass', shortName: 'Mirrors' }
    ]
  }
]

export const CONDITION_GRADES: ConditionGrade[] = [
  { code: 'OG', name: 'OEM Genuine', badge: 'OEM Genuine', description: 'Factory original equipment directly from manufacturer' },
  { code: 'AM', name: 'Aftermarket Certified', badge: 'Certified AM', description: 'ISO/TS 16949 certified tier-1 replacement component' },
  { code: 'RB', name: 'Remanufactured', badge: 'Remanufactured', description: 'Rebuilt to OE tolerances with renewed wearing parts' },
  { code: 'TK', name: 'Tokunbo / Tested Salvage', badge: 'Tokunbo Grade-A', description: 'Direct import tested salvage with verified operational integrity' }
]

export const MAKE_CODES: Record<string, string> = {
  toyota: 'TY',
  lexus: 'LX',
  honda: 'HN',
  acura: 'AC',
  nissan: 'NS',
  infiniti: 'IN',
  mercedes: 'MB',
  'mercedes-benz': 'MB',
  bmw: 'BM',
  audi: 'AU',
  volkswagen: 'VW',
  ford: 'FD',
  hyundai: 'HY',
  kia: 'KA',
  mitsubishi: 'MS',
  peugeot: 'PG',
  landrover: 'LR',
  'land-rover': 'LR',
  mazda: 'MZ',
  suzuki: 'SZ',
  subaru: 'SB',
  chevrolet: 'CH',
  isuzu: 'IS',
  renault: 'RN'
}

export const MODEL_CODES: Record<string, string> = {
  corolla: 'CO',
  camry: 'CA',
  hilux: 'HX',
  'land cruiser': 'LC',
  prado: 'PR',
  highlander: 'HL',
  rav4: 'RA',
  yaris: 'YA',
  avensis: 'AV',
  sienna: 'SI',
  matrix: 'MX',
  civic: 'CI',
  accord: 'AC',
  'cr-v': 'CR',
  crv: 'CR',
  pilot: 'PI',
  altima: 'AL',
  sentra: 'SE',
  maxima: 'MA',
  rogue: 'RO',
  'c-class': 'CC',
  'e-class': 'EC',
  '3 series': '3S',
  '5 series': '5S'
}

export function normalizeVendorAbbreviation(vendor = '', fallback = 'AB'): string {
  const clean = String(vendor || '')
    .trim()
    .toUpperCase()
    .replace(/^SEL_|^ORG_/, '')
    .replace(/[^A-Z0-9]/g, '')
  if (!clean) return fallback
  return clean.slice(0, 4)
}

export function normalizeCountry(country = '', fallback = 'GH'): string {
  const clean = String(country || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
  if (clean.length === 2) return clean
  if (clean.includes('GHANA') || clean === 'GHS') return 'GH'
  if (clean.includes('NIGERIA') || clean === 'NGN') return 'NG'
  if (clean.includes('KENYA') || clean === 'KES') return 'KE'
  return fallback
}

export function normalizeRegion(region = '', countryCode = 'GH', fallback = 'ACC'): string {
  const clean = String(region || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
  if (clean.length >= 3) return clean.slice(0, 3)
  if (countryCode === 'NG') return 'LAG'
  if (countryCode === 'KE') return 'NBO'
  return fallback
}

export function normalizeMakeCode(make = ''): string {
  const clean = String(make || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '')
  if (MAKE_CODES[clean]) return MAKE_CODES[clean]
  if (clean.length === 2) return clean.toUpperCase()
  return (clean.slice(0, 2) || 'TY').toUpperCase()
}

export function normalizeModelCode(model = ''): string {
  const clean = String(model || '').trim().toLowerCase()
  if (MODEL_CODES[clean]) return MODEL_CODES[clean]
  const alpha = clean.replace(/[^a-z0-9]/g, '')
  if (alpha.length >= 2) return alpha.slice(0, 2).toUpperCase()
  return 'CO'
}

export function normalizeYearCode(year: any = ''): string {
  const digits = String(year || '').trim().replace(/[^0-9]/g, '')
  if (digits.length >= 2) return digits.slice(-2)
  return '17'
}

export function resolveCategoryAndSubcategory(category = 'BDY', subcategory = 'BMP') {
  const catInput = String(category || '').trim().toUpperCase()
  const subInput = String(subcategory || '').trim().toUpperCase()

  let foundCategory = MMYTE_CATEGORIES.find((c) => c.code === catInput || c.name.toUpperCase().includes(catInput))
  let catCode = foundCategory ? foundCategory.code : (catInput.slice(0, 3) || 'BDY')

  let subCode = 'BMP'
  if (foundCategory) {
    const foundSub = foundCategory.subcategories.find((s) =>
      s.code === subInput ||
      s.legacyCode === subInput ||
      s.name.toUpperCase().includes(subInput) ||
      s.shortName?.toUpperCase() === subInput
    )
    if (foundSub) {
      subCode = foundSub.code
    } else if (subInput.length >= 3) {
      subCode = subInput.slice(0, 3)
    }
  }

  return {
    categoryCode: catCode.padEnd(3, 'X').slice(0, 3),
    subcategoryCode: subCode.padEnd(3, 'X').slice(0, 3)
  }
}

export function generateUniqueSku(args: any = {}): string {
  const prefix = SKU_PREFIX
  const vAbbr = normalizeVendorAbbreviation(args.vendorAbbreviation || args.sellerId || 'AB')
  const cCode = normalizeCountry(args.country || args.countryCode || args.geo || 'GH')
  const rCode = normalizeRegion(args.region || args.state, cCode)
  const mCode = normalizeMakeCode(args.make || 'TY')
  const modCode = normalizeModelCode(args.model || 'CO')
  const yCode = normalizeYearCode(args.year || '17')
  const { categoryCode: catFinal, subcategoryCode: subFinal } = resolveCategoryAndSubcategory(
    args.category || args.categoryCode || 'BDY',
    args.subcategory || args.subcategoryCode || 'BMP'
  )

  return `${prefix}-${vAbbr}-${cCode}-${rCode}-${mCode}-${modCode}-${yCode}-${catFinal}-${subFinal}`
}

export function validateUniqueSku(sku = ''): boolean {
  const pattern = /^P4D-[A-Z0-9]{2,4}-[A-Z]{2}-[A-Z0-9]{2,4}-[A-Z0-9]{2}-[A-Z0-9]{2}-[0-9]{2}-[A-Z]{3}-[A-Z0-9]{3}$/
  return pattern.test(String(sku || '').trim().toUpperCase())
}

export function parseEnterpriseSku(sku = '') {
  const cleanSku = String(sku || '').trim().toUpperCase()
  const parts = cleanSku.split('-')

  if (parts.length === 9 && parts[0] === SKU_PREFIX) {
    const [prefix, vendorAbbreviation, country, region, make, model, year, category, subcategory] = parts
    return {
      isValid: true,
      standard: 'P4D_UNIQUE_V2',
      prefix,
      vendorAbbreviation,
      country,
      region,
      make,
      model,
      year: `20${year}`,
      yearShort: year,
      category,
      subcategory,
      raw: cleanSku
    }
  }

  if (parts.length === 6) {
    const [geo, sellerCode, mmyteHash, catSub, partId, grade] = parts
    const gradeObj = CONDITION_GRADES.find((g) => g.code === grade) || { code: grade, name: grade, badge: grade, description: '' }
    return {
      isValid: true,
      standard: 'LEGACY_V1',
      geo,
      sellerCode,
      mmyteHash,
      catSub,
      partId,
      grade: gradeObj,
      raw: cleanSku
    }
  }

  return { isValid: false, raw: sku }
}

export function generateEnterpriseSku(args: any = {}) {
  return generateUniqueSku(args)
}

export function resolveProductSku(product: any): string {
  if (!product) return 'P4D-AB-GH-ACC-TY-CO-17-BDY-BMP'
  const existingSku = product.sku || product.product_sku || product.mpn || ''
  if (validateUniqueSku(existingSku)) {
    return existingSku
  }

  return generateUniqueSku({
    sellerId: product.seller_id || product.sellerId || product.org_id || product.orgId || product.seller?.id || 'AB',
    country: product.country || 'NG',
    region: product.region || product.state || 'LAG',
    make: product.make || product.mmyt_make || 'TY',
    model: product.model || product.mmyt_model || 'CO',
    year: product.year || product.mmyt_year || '17',
    category: product.category || 'BRK',
    subcategory: product.subcategory || 'BMP'
  })
}
